import React, { useEffect, useState } from 'react';
import { TagIcon, SparklesIcon, CheckCircleIcon, RotateCcwIcon } from 'lucide-react';
import { LoyaltyOnboardingBottomSheet } from '../modals/LoyaltyOnboardingBottomSheet';
interface LoyaltyPointsProps {
  points: number;
}
export function LoyaltyPoints({
  points
}: LoyaltyPointsProps) {
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [loyaltyProgress, setLoyaltyProgress] = useState(0);
  const [showResetButton, setShowResetButton] = useState(false);
  // Check enrollment status from localStorage
  const checkEnrollmentStatus = () => {
    const savedData = localStorage.getItem('loyaltyOnboarding');
    console.log('Checking enrollment status:', savedData);
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        const requiredFields = [data.gender, data.maritalStatus, data.nationality, data.dateOfBirth, data.workingSector, data.city, data.district, data.preferredLanguage];
        const completedRequired = requiredFields.filter(field => field !== '' && field !== undefined && field !== null).length;
        const progress = Math.round(completedRequired / requiredFields.length * 100);
        console.log('Loyalty progress:', progress);
        setLoyaltyProgress(progress);
        setIsEnrolled(progress === 100);
      } catch (error) {
        console.error('Error parsing loyalty data:', error);
        setIsEnrolled(false);
        setLoyaltyProgress(0);
      }
    } else {
      console.log('No loyalty data found');
      setIsEnrolled(false);
      setLoyaltyProgress(0);
    }
  };
  useEffect(() => {
    checkEnrollmentStatus();
    // Listen for custom event
    const handleLoyaltyUpdate = () => {
      checkEnrollmentStatus();
    };
    window.addEventListener('loyaltyUpdated', handleLoyaltyUpdate);
    // Listen for storage changes (for cross-tab updates)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'loyaltyOnboarding') {
        checkEnrollmentStatus();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('loyaltyUpdated', handleLoyaltyUpdate);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  const handleComplete = (data: any) => {
    localStorage.setItem('loyaltyOnboarding', JSON.stringify(data));
    checkEnrollmentStatus();
    setIsBottomSheetOpen(false);
    // Dispatch custom event to update all LoyaltyPoints components
    window.dispatchEvent(new Event('loyaltyUpdated'));
  };
  // Reset loyalty data (for testing)
  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset your loyalty data? This is for testing purposes only.')) {
      localStorage.removeItem('loyaltyOnboarding');
      setIsEnrolled(false);
      setLoyaltyProgress(0);
      window.dispatchEvent(new Event('loyaltyUpdated'));
      setShowResetButton(false);
    }
  };
  // Show reset button on Ctrl/Cmd + Shift + Click
  const handleContainerClick = (e: React.MouseEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.shiftKey) {
      setShowResetButton(!showResetButton);
    }
  };
  // If enrolled, show advanced loyalty card
  if (isEnrolled) {
    return <>
        <div onClick={handleContainerClick} className="relative" data-id="element-5474" data-mp="lmjcn2yrt2fu">
          <div className="bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] border-2 border-[#FFD700] rounded-lg p-4 shadow-lg" data-id="element-5475" data-mp="gnoix5z9ltgy">
            <div className="flex items-center gap-2 mb-3" data-id="element-5476" data-mp="vddxc2wdbtz6">
              <SparklesIcon className="w-5 h-5 text-[#FFD700]" data-id="element-5477" data-mp="qkkqn9hcabkg" />
              <div className="flex-1" data-id="element-5478" data-mp="qj9otf7gfdvz">
                <div className="flex items-center gap-2" data-id="element-5479" data-mp="2uqgx0sbh34f">
                  <p className="text-sm font-bold text-white" data-id="element-5480" data-mp="2t5lza23bi61">
                    Loyalty Member Active
                  </p>
                  <CheckCircleIcon className="w-4 h-4 text-green-400" data-id="element-5481" data-mp="2xaoak3bzwm1i" />
                </div>
                <p className="text-xs text-blue-200" data-id="element-5482" data-mp="3299f2eya5rvr">
                  Earning rewards on every purchase
                </p>
              </div>
            </div>
            <div className="bg-white bg-opacity-10 rounded-lg p-3 backdrop-blur-sm" data-id="element-5483" data-mp="369tcljmrmxif">
              <div className="flex items-center justify-between" data-id="element-5484" data-mp="3b3e18l2gyf4y">
                <div data-id="element-5485" data-mp="3g1paatkxwlm4">
                  <p className="text-xs text-blue-200 mb-1" data-id="element-5486" data-mp="3g04gx7ad38pu">You will earn</p>
                  <p className="text-2xl font-bold text-[#FFD700]" data-id="element-5487" data-mp="3kz7niw5zpmzx">
                    {points} points
                  </p>
                </div>
                <div className="bg-[#FFD700] bg-opacity-20 rounded-full p-3" data-id="element-5488" data-mp="3pwzdhkn2qz1h">
                  <TagIcon className="w-6 h-6 text-[#FFD700]" data-id="element-5489" data-mp="3u05fuh72g62o" />
                </div>
              </div>
            </div>
            <p className="text-xs text-blue-100 mt-3 text-center" data-id="element-5490" data-mp="1vn5iatgdoijl">
              Points will be added after purchase confirmation
            </p>
          </div>
          {/* Reset Button - Only visible when triggered */}
          {showResetButton && <button onClick={handleReset} className="absolute -bottom-10 left-0 right-0 mx-auto w-max bg-red-600 hover:bg-red-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg flex items-center gap-1 shadow-lg transition-colors" data-id="element-5491" data-mp="1nbdjwgpzzgai">
              <RotateCcwIcon className="w-3 h-3" data-id="element-5492" data-mp="2b2iyksk7c2j8" />
              Reset Loyalty Data (Testing)
            </button>}
        </div>
        <LoyaltyOnboardingBottomSheet isOpen={isBottomSheetOpen} onClose={() => setIsBottomSheetOpen(false)} onComplete={handleComplete} data-id="element-5493" data-mp="263fsd4shjq9o" />
      </>;
  }
  // If not enrolled, show simple join prompt
  return <>
      <div onClick={handleContainerClick} data-id="element-5494" data-mp="qmp3fotxk137">
        <button onClick={() => setIsBottomSheetOpen(true)} className="w-full bg-yellow-50 border border-yellow-200 rounded-lg p-4 hover:bg-yellow-100 transition-all hover:shadow-md" data-id="element-5495" data-mp="qnqpspgeca08">
          <div className="flex items-center gap-2" data-id="element-5496" data-mp="n51h29ro9x22">
            <TagIcon className="w-5 h-5 text-yellow-600" data-id="element-5497" data-mp="s3l1g5qmb0sb" />
            <div className="flex-1 text-left" data-id="element-5498" data-mp="s0yw8cr78xd9">
              <p className="text-sm font-medium text-gray-900" data-id="element-5499" data-mp="e2idtojz5ifa">
                Earn {points} points on this item
              </p>
              <p className="text-xs text-gray-600" data-id="element-5500" data-mp="i3c2wl95l39r">
                Join our loyalty program to earn rewards
              </p>
            </div>
            <div className="ml-auto bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-bold px-4 py-2 rounded-lg transition-colors whitespace-nowrap" data-id="element-5501" data-mp="94yzypr6v7nv">
              Join Now
            </div>
          </div>
        </button>
      </div>
      <LoyaltyOnboardingBottomSheet isOpen={isBottomSheetOpen} onClose={() => setIsBottomSheetOpen(false)} onComplete={handleComplete} data-id="element-5502" data-mp="3vkl2wnq0kd73" />
    </>;
}